/**
 * Loyalty elements – cart banner and header badge.
 * Data from window.globals and DotApparel rewards API (loyalty-account.ts).
 */

import {
  fetchRewardsAccount,
  getCachedRewardsAccount,
  getAccountFromResult,
  type RewardsAccountReward,
  type RewardsAccountResponse
} from '@/components/libs/dotapparel/loyalty-account'
import { formatMoney } from '@/components/helpers/currency-formatter'

// --- Shared types and helpers ---

type LoyaltyBannerState = 'logged-out' | 'logged-in-no-rewards' | 'logged-in-rewards' | 'unavailable'
type LoyaltyBannerSize = 'large' | 'small'
type LoyaltyBannerConditional = 'logged-out' | 'logged-in' | 'logged-in-rewards' | 'logged-in-no-rewards'

interface LoyaltyTierMeta {
  tier_name: string
  min_threshold: number
  id?: number | null
  gid?: string
  tier_colour?: string | { value?: string }
  text_colour?: string | { value?: string }
}

interface RichTextNode {
  type?: string
  value?: string
  bold?: boolean
  children?: RichTextNode[]
}

interface MetaRewardRaw {
  title?: string | { value?: string }
  body?: string | { value?: string } | RichTextNode
  image?: { value?: { url?: string }; url?: string } | string
  tiers?: string[]
  ap21_loyalty_description?: string | { value?: string }
  background_colour?: string | { value?: string }
  text_colour?: string | { value?: string }
  cta_text?: string | { value?: string }
  cta_url?: string | { value?: string }
  priority?: number | { value?: number }
}

interface DisplayReward {
  priority: number
  title: string
  body: string
  imageUrl: string
  backgroundColour: string
  textColour: string
  ctaText: string
  ctaUrl: string
  expiresAt: string
}

interface Globals {
  customer?: { id?: string; tierName?: string; email?: string; availableRewards?: string }
  cart?: { initCart?: { total_price?: number }; loyalty?: { tooltipText?: string } }
  routes?: { register_url?: string; login_url?: string }
  shopify?: { moneyFormat?: string }
  metaobjects?: {
    tiers?: LoyaltyTierMeta[]
    rewards?: MetaRewardRaw[]
    loyalty_rewards?: MetaRewardRaw[]
  }
}

function getMetaNumber(v: number | { value?: number } | undefined): number {
  if (v == null) return 0
  return typeof v === 'number' ? v : (v.value ?? 0) || 0
}

function escapeHtml(text: string): string {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

function getGlobals(): Globals | undefined {
  return typeof window !== 'undefined' ? (window as any).globals : undefined
}

function getCartTotalCents(): number {
  const cart = getGlobals()?.cart?.initCart
  const total = cart?.total_price
  return typeof total === 'number' && total >= 0 ? total : 0
}

function hasLoyaltyData(account: { tier_name?: string } | null): boolean {
  return Boolean(account?.tier_name?.trim())
}

// --- Loyalty Cart Banner ---

function getCartBannerStateTemplates(opts: {
  registerUrl: string
  loginUrl: string
  tooltipText: string
  size: LoyaltyBannerSize
  rewardsFormatted: string
  progressPercent: number
  rewardsBannerBg: string
  rewardsBannerMessage: string
}) {
  const {
    registerUrl,
    loginUrl,
    tooltipText,
    size,
    rewardsFormatted,
    progressPercent,
    rewardsBannerBg,
    rewardsBannerMessage
  } = opts
  const safeRegister = escapeHtml(registerUrl)
  const safeLogin = escapeHtml(loginUrl)
  const spacing = size === 'large' ? 'p-1 gap-x-1' : 'p-0.5 gap-x-0.5'

  return {
    'logged-out': `
      <div class="flex flex-wrap items-start justify-between rounded-8 ${spacing} gap-y-0.125 bg-brand-linen">
        <div class="group/loyalty-tooltip shrink-0 flex items-center gap-x-0.5">
          <div class="shrink-0 w-[13px] h-[12px] children:size-full">
            <svg xmlns="http://www.w3.org/2000/svg" class="icon" fill="none"><use width="100%" height="100%" href="#diamond" x="0" y="0" /></svg>
          </div>
          <p class="paragraph-s font-medium">Loyalty Rewards</p>
          ${
            tooltipText
              ? `
          <div class="relative">
            <div class="size-[15px] children:size-full">
              <svg xmlns="http://www.w3.org/2000/svg" class="icon" fill="none"><use width="100%" height="100%" href="#info" x="0" y="0" /></svg>
            </div>
            <div class="absolute -top-1 right-0 lg:right-unset lg:left-1/2 lg:-translate-x-1/2 -translate-y-full w-[260px] h-fit text-brand-black bg-neutral-light p-1 rounded-8 opacity-0 group-hover/loyalty-tooltip:opacity-100 invisible group-hover/loyalty-tooltip:visible transition-all before:content-[''] before:absolute before:bottom-0 before:left-1/2 before:-translate-x-1/2 before:translate-y-1/2 before:bg-neutral-light before:size-[16px] before:rotate-45">
              <div class="richtext paragraph-s">${tooltipText}</div>
            </div>
          </div>
          `
              : ''
          }
        </div>
        <div>
          <p class="paragraph-s">
            <a href="${safeRegister}" class="text-line link-fade" aria-label="Join Loyalty">Join</a>
            or
            <a href="${safeLogin}" class="text-line link-fade" aria-label="Login to dashboard">login</a>
          </p>
        </div>
      </div>
    `,

    'logged-in-rewards': `
      <div class="flex items-start justify-between rounded-8 ${spacing} bg-system-success-light overflow-hidden">
        <div class="flex items-center gap-x-0.5">
          <div class="shrink-0 w-[13px] h-[12px] children:size-full">
            <svg xmlns="http://www.w3.org/2000/svg" class="icon" fill="none"><use width="100%" height="100%" href="#diamond" x="0" y="0" /></svg>
          </div>
          <p class="paragraph-s"><span class="font-medium">Available Rewards at checkout:</span> ${escapeHtml(
            rewardsFormatted
          )}</p>
        </div>
      </div>
    `,

    'logged-in-no-rewards': `
      <div class="flex flex-col rounded-8 ${rewardsBannerBg} overflow-hidden transition-all">
        <div class="w-full h-[4px] bg-system-warning/10 overflow-hidden rounded-full">
          <div class="h-full transition-all ${
            progressPercent >= 100 ? 'bg-system-success' : 'bg-system-warning'
          }" style="width: ${Math.round(progressPercent)}%"></div>
        </div>
        <div class="flex items-start justify-between ${spacing}">
          <div class="flex items-center gap-x-0.5">
            <div class="shrink-0 w-[13px] h-[12px] children:size-full">
              <svg xmlns="http://www.w3.org/2000/svg" class="icon" fill="none"><use width="100%" height="100%" href="#diamond" x="0" y="0" /></svg>
            </div>
            <p class="paragraph-s">${rewardsBannerMessage}</p>
          </div>
        </div>
      </div>
    `,

    unavailable: `
      <div class="flex flex-wrap items-start justify-between rounded-8 ${spacing} gap-y-0.125 bg-brand-linen overflow-hidden">
        <div class="flex items-center gap-x-0.5">
          <div class="shrink-0 w-[13px] h-[12px] children:size-full">
            <svg xmlns="http://www.w3.org/2000/svg" class="icon" fill="none"><use width="100%" height="100%" href="#diamond" x="0" y="0" /></svg>
          </div>
          <p class="paragraph-s">Your loyalty data is currently unavailable. Please contact support.</p>
        </div>
      </div>
    `
  }
}

export class LoyaltyCartBannerElement extends HTMLElement {
  private state: LoyaltyBannerState = 'logged-out'
  private size: LoyaltyBannerSize = 'large'
  private conditional: LoyaltyBannerConditional | null = null
  private conditionalReady = false
  private registerUrl = ''
  private loginUrl = ''
  private tooltipText = ''
  private rewardsFormatted = ''
  private spendFormatted = ''
  private progressPercent = 100
  private rewardsBannerBg = 'bg-brand-linen'
  private rewardsBannerMessage = ''

  connectedCallback() {
    this.parseSize()
    this.parseConditional()
    if (this.conditional !== null) {
      this.style.display = 'none'
    }
    this.resolveStateFromGlobals()
    if (this.conditional === 'logged-out') {
      this.conditionalReady = true
    }
    const isLoggedIn = this.state !== 'logged-out'
    if (isLoggedIn) {
      this.innerHTML = ''
      this.style.display = 'none'
      this.maybeFetchRewardsAndUpdate()
    } else {
      this.render()
      this.maybeFetchRewardsAndUpdate()
    }
  }

  private parseSize() {
    const size = this.getAttribute('data-size')
    if (size === 'small' || size === 'large') {
      this.size = size
    }
  }

  private parseConditional() {
    const value = this.getAttribute('data-conditional')
    if (
      value === 'logged-out' ||
      value === 'logged-in' ||
      value === 'logged-in-rewards' ||
      value === 'logged-in-no-rewards'
    ) {
      this.conditional = value
    }
  }

  private shouldShow(): boolean {
    if (this.conditional === null) return true
    if (this.conditional === 'logged-out') return this.state === 'logged-out'
    if (this.conditional === 'logged-in') { return this.state === 'logged-in-rewards' || this.state === 'logged-in-no-rewards' || this.state === 'unavailable' }
    return this.state === this.conditional
  }

  private resolveStateFromGlobals() {
    const g = getGlobals()
    const customer = g?.customer
    const cart = g?.cart
    const routes = g?.routes
    const isLoggedIn = Boolean(customer?.id)

    this.registerUrl = routes?.register_url || '/account/register'
    this.loginUrl = routes?.login_url || '/account/login'
    this.tooltipText = cart?.loyalty?.tooltipText ?? ''

    if (!isLoggedIn) {
      this.state = 'logged-out'
      return
    }

    const email = customer?.email ?? ''
    const account = email ? getCachedRewardsAccount(email) : null
    this.applyRewardsState(account)
  }

  private applyRewardsState(
    account: {
      available_rewards?: string
      spend_to_next_tier?: string
      next_tier_name?: string
      tier_name?: string
    } | null
  ) {
    if (!hasLoyaltyData(account)) {
      this.state = 'unavailable'
      return
    }

    const raw = account?.available_rewards?.trim() ?? ''
    const amount = raw === '' ? 0 : parseFloat(raw)
    const g = getGlobals()
    const moneyFormat = g?.shopify?.moneyFormat ?? '${{amount}}'

    const spendRaw = account?.spend_to_next_tier?.trim() ?? ''
    const spendAmountDollars = spendRaw === '' ? 0 : parseFloat(spendRaw)
    const cartTotalCents = getCartTotalCents()
    const cartTotalDollars = cartTotalCents / 100
    const spendAfterCartDollars = spendAmountDollars - cartTotalDollars
    const hasSpendToNext = !Number.isNaN(spendAfterCartDollars) && spendAfterCartDollars > 0

    this.spendFormatted =
      !hasSpendToNext || Number.isNaN(spendAfterCartDollars)
        ? ''
        : formatMoney(Math.max(0, Math.round(spendAfterCartDollars * 100)), moneyFormat as string)

    this.rewardsBannerBg = hasSpendToNext ? 'bg-brand-linen' : 'bg-system-success-light'
    const nextTier = (account?.next_tier_name ?? '').trim()
    const tierName = (account?.tier_name ?? '').trim()
    const wrapMedium = (text: string) => `<span class="font-medium">${escapeHtml(text)}</span>`

    if (spendAfterCartDollars <= 0 || Number.isNaN(spendAfterCartDollars)) {
      if (nextTier) {
        this.rewardsBannerMessage = `You'll unlock the ${wrapMedium(nextTier)} with this purchase`
      } else {
        this.rewardsBannerMessage = `You are currently in the ${wrapMedium(tierName || 'top tier')}`
      }
    } else {
      this.rewardsBannerMessage = `Spend to reach next tier after this purchase: ${escapeHtml(this.spendFormatted)}`
    }

    this.progressPercent = this.computeTierProgressPercent(
      account?.spend_to_next_tier,
      account?.next_tier_name,
      g?.metaobjects?.tiers,
      cartTotalCents
    )

    if (!Number.isNaN(amount) && amount > 0) {
      this.state = 'logged-in-rewards'
      const cents = Math.round(amount * 100)
      this.rewardsFormatted = formatMoney(cents, moneyFormat as string)
    } else {
      this.state = 'logged-in-no-rewards'
    }
  }

  /**
   * Progress toward next tier including current cart: 1 - (spend_to_next_tier_cents - cart_total_cents) / min_threshold.
   * spend_to_next_tier from API is in dollars; metaobject min_threshold is in cents; cart total is in cents.
   * If spend remaining after cart <= 0, cap at 100%.
   */
  private computeTierProgressPercent(
    spendToNextTier: string | undefined,
    nextTierName: string | undefined,
    tiers: LoyaltyTierMeta[] | undefined,
    cartTotalCents: number
  ): number {
    const spendDollars = parseFloat(spendToNextTier ?? '') || 0
    if (spendDollars <= 0 || !nextTierName || !tiers?.length) return 100

    const tier = tiers.find((t) => t.tier_name === nextTierName)
    if (!tier || tier.min_threshold <= 0) return 100

    const spendCents = Math.round(spendDollars * 100)
    const spendRemainingCents = spendCents - cartTotalCents
    if (spendRemainingCents <= 0) return 100

    const ratio = 1 - spendRemainingCents / tier.min_threshold
    const percent = ratio * 100
    return Math.max(0, Math.min(100, percent))
  }

  private async maybeFetchRewardsAndUpdate() {
    const email = getGlobals()?.customer?.email ?? ''
    if (!email) {
      if (this.conditional !== null) {
        this.conditionalReady = true
        this.render()
      }
      return
    }

    const result = await fetchRewardsAccount(email)
    const account = getAccountFromResult(result)
    this.applyRewardsState(!result.ok ? null : account)
    if (this.conditional !== null) {
      this.conditionalReady = true
    }
    this.render()
  }

  private render() {
    if (this.conditional !== null && !this.conditionalReady) {
      this.innerHTML = ''
      this.style.display = 'none'
      return
    }
    if (!this.shouldShow()) {
      this.innerHTML = ''
      this.style.display = 'none'
      return
    }
    this.style.display = ''
    const templates = getCartBannerStateTemplates({
      registerUrl: this.registerUrl,
      loginUrl: this.loginUrl,
      tooltipText: this.tooltipText,
      size: this.size,
      rewardsFormatted: this.rewardsFormatted,
      progressPercent: this.progressPercent,
      rewardsBannerBg: this.rewardsBannerBg,
      rewardsBannerMessage: this.rewardsBannerMessage
    })
    this.innerHTML = templates[this.state].trim()
  }
}

// --- Loyalty Rewards or Spend (account page card) ---

const REWARDS_OR_SPEND_CONTAINER_CLASS = 'flex flex-col items-center justify-center gap-y-0.75 w-full'

/**
 * Single card that shows either:
 * - Loading: white box with spinner
 * - Rewards: when customer has surfaceable available_rewards (monetary value + link to rewards)
 * - Spend to next tier: default when no rewards (flat `spend_to_next_tier` from API; no cart adjustment)
 * Used on the loyalty account page; expects customer to be logged in.
 */
export class LoyaltyRewardsSpendDataElement extends HTMLElement {
  private state: 'loading' | 'rewards' | 'spend' | 'unavailable' = 'loading'
  private rewardsFormatted = ''
  private spendFormatted = ''
  private progressPercent = 100
  private spendMessage = ''
  private rewardsGridUrl = ''

  connectedCallback() {
    this.rewardsGridUrl = (this.getAttribute('data-rewards-grid-url') ?? '').trim()
    this.renderLoading()
    this.fetchAndRender()
  }

  private applyAccount(
    account: {
      available_rewards?: string
      spend_to_next_tier?: string
      next_tier_name?: string
      tier_name?: string
    } | null
  ) {
    if (!hasLoyaltyData(account)) {
      this.state = 'unavailable'
      return
    }

    const g = getGlobals()
    const moneyFormat = g?.shopify?.moneyFormat ?? '${{amount}}'

    const raw = account?.available_rewards?.trim() ?? ''
    const amount = raw === '' ? 0 : parseFloat(raw)
    if (!Number.isNaN(amount) && amount > 0) {
      this.state = 'rewards'
      const cents = Math.round(amount * 100)
      this.rewardsFormatted = formatMoney(cents, moneyFormat as string)
      return
    }

    this.state = 'spend'
    const spendRaw = account?.spend_to_next_tier?.trim() ?? ''
    const spendAmountDollars = spendRaw === '' ? 0 : parseFloat(spendRaw)
    const hasReachedNextTier = Number.isNaN(spendAmountDollars) || spendAmountDollars <= 0

    const nextTier = (account?.next_tier_name ?? '').trim()
    const tierName = (account?.tier_name ?? '').trim()
    const wrapMedium = (text: string) => `<span class="font-medium">${escapeHtml(text)}</span>`

    if (hasReachedNextTier) {
      this.spendFormatted = ''
      if (nextTier) {
        this.spendMessage = `You'll unlock the ${wrapMedium(nextTier)} shortly`
      } else {
        this.spendMessage = `You are currently in the ${wrapMedium(tierName || 'top tier')}`
      }
      this.progressPercent = 100
      return
    }

    this.spendFormatted = formatMoney(
      Math.max(0, Math.round(spendAmountDollars * 100)),
      moneyFormat as string
    )
    this.spendMessage = ''

    this.progressPercent = this.computeTierProgressPercent(
      account?.spend_to_next_tier,
      account?.next_tier_name,
      g?.metaobjects?.tiers
    )
  }

  private computeTierProgressPercent(
    spendToNextTier: string | undefined,
    nextTierName: string | undefined,
    tiers: LoyaltyTierMeta[] | undefined
  ): number {
    const spendDollars = parseFloat(spendToNextTier ?? '') || 0
    if (spendDollars <= 0 || !nextTierName || !tiers?.length) return 100

    const tier = tiers.find((t) => t.tier_name === nextTierName)
    if (!tier || tier.min_threshold <= 0) return 100

    const spendRemainingCents = Math.round(spendDollars * 100)
    if (spendRemainingCents <= 0) return 100

    const ratio = 1 - spendRemainingCents / tier.min_threshold
    const percent = ratio * 100
    return Math.max(0, Math.min(100, percent))
  }

  private async fetchAndRender() {
    const email = getGlobals()?.customer?.email ?? ''
    if (!email) {
      this.state = 'unavailable'
      this.render()
      return
    }

    const account = getCachedRewardsAccount(email)
    if (account && hasLoyaltyData(account)) {
      this.applyAccount(account)
      this.render()
      return
    }

    const result = await fetchRewardsAccount(email)
    const data = !result.ok ? null : getAccountFromResult(result)
    this.applyAccount(data)
    this.render()
  }

  private renderLoading() {
    this.innerHTML = `
      <div class="${REWARDS_OR_SPEND_CONTAINER_CLASS}">
        <div class="flex items-center justify-center flex-1 w-full">
          <div class="size-[24px] text-brand-black" aria-hidden="true">
            <svg class="size-full animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        </div>
      </div>
    `
  }

  private render() {
    if (this.state === 'loading') {
      this.renderLoading()
      return
    }

    if (this.state === 'unavailable') {
      this.innerHTML = `
        <div class="${REWARDS_OR_SPEND_CONTAINER_CLASS}">
          <p class="paragraph-s">Your loyalty data is currently unavailable</p>
        </div>
      `
      return
    }

    if (this.state === 'rewards') {
      const rewardsLinkBlock =
        this.rewardsGridUrl !== ''
          ? `<a href="${escapeHtml(
              this.rewardsGridUrl
            )}" class="btn btn-primary btn-text btn-s w-fit" aria-label="View your rewards">
            <span>View Your Rewards</span>
          </a>`
          : ''
      this.innerHTML = `
        <div class="${REWARDS_OR_SPEND_CONTAINER_CLASS}">
          <span class="paragraph-s font-heading font-medium uppercase">Your Current Rewards</span>
          <div class="flex items-center gap-x-0.5">
            <p class="h2">${escapeHtml(this.rewardsFormatted)}</p>
          </div>
          <p class="paragraph-s">View rewards below for expiry dates</p>
          ${rewardsLinkBlock}
        </div>
      `
      return
    }

    this.innerHTML = `
      <div class="${REWARDS_OR_SPEND_CONTAINER_CLASS}">
        <span class="paragraph-s font-heading font-medium uppercase">Spend To Reach Next Tier</span>
        <div class="flex items-center gap-x-0.5">
          <p class="h2">${this.spendFormatted ? escapeHtml(this.spendFormatted) : '—'}</p>
        </div>
        <div class="w-full max-w-[308px] h-[4px] bg-system-warning/10 overflow-hidden rounded-full">
          <div class="h-full transition-all ${
            this.progressPercent >= 100 ? 'bg-system-success' : 'bg-system-warning'
          }" style="width: ${Math.round(this.progressPercent)}%"></div>
        </div>
        ${this.spendMessage ? `<p class="paragraph-s">${this.spendMessage}</p>` : ''}
      </div>
    `
  }
}

// --- Loyalty Tier + Spend Data (account page tier card with built-in progress bar) ---

const TIER_SPEND_INNER_CLASS = 'flex flex-col items-center justify-center gap-y-0.75 w-full'

export class LoyaltyTierSpendDataElement extends HTMLElement {
  private state: 'loading' | 'ready' | 'unavailable' = 'loading'
  private tierName = ''
  private tierImageUrl = ''
  private benefitsTableUrl = ''
  private hasAvailableRewards = false
  private spendFormatted = ''
  private nextTierName = ''
  private progressPercent = 100

  connectedCallback() {
    this.tierImageUrl = this.getAttribute('data-tier-image-url') ?? ''
    this.benefitsTableUrl = (this.getAttribute('data-benefits-table-url') ?? '').trim()
    this.renderLoading()
    this.fetchAndRender()
  }

  private applyAccount(
    account: {
      tier_name?: string
      available_rewards?: string
      spend_to_next_tier?: string
      next_tier_name?: string
    } | null
  ) {
    if (!hasLoyaltyData(account)) {
      this.state = 'unavailable'
      this.hasAvailableRewards = false
      return
    }

    this.tierName = (account?.tier_name ?? '').trim()
    this.nextTierName = (account?.next_tier_name ?? '').trim()

    const rewardsRaw = account?.available_rewards?.trim() ?? ''
    const rewardsAmount = rewardsRaw === '' ? 0 : parseFloat(rewardsRaw)
    this.hasAvailableRewards = !Number.isNaN(rewardsAmount) && rewardsAmount > 0

    const g = getGlobals()
    const moneyFormat = g?.shopify?.moneyFormat ?? '${{amount}}'
    const spendRaw = account?.spend_to_next_tier?.trim() ?? ''
    const spendAmountDollars = spendRaw === '' ? 0 : parseFloat(spendRaw)
    const hasReachedNextTier = Number.isNaN(spendAmountDollars) || spendAmountDollars <= 0

    if (hasReachedNextTier) {
      this.spendFormatted = ''
      this.progressPercent = 100
      this.state = 'ready'
      return
    }

    const spendCents = Math.round(spendAmountDollars * 100)
    this.spendFormatted = formatMoney(spendCents, moneyFormat as string)

    this.progressPercent = this.computeTierProgressPercent(
      account?.spend_to_next_tier,
      account?.next_tier_name,
      g?.metaobjects?.tiers
    )
    this.state = 'ready'
  }

  private computeTierProgressPercent(
    spendToNextTier: string | undefined,
    nextTierName: string | undefined,
    tiers: LoyaltyTierMeta[] | undefined
  ): number {
    const spendDollars = parseFloat(spendToNextTier ?? '') || 0
    if (spendDollars <= 0 || !nextTierName || !tiers?.length) return 100

    const tier = tiers.find((t) => t.tier_name === nextTierName)
    if (!tier || tier.min_threshold <= 0) return 100

    const spendCents = Math.round(spendDollars * 100)
    const ratio = 1 - spendCents / tier.min_threshold
    const percent = ratio * 100
    return Math.max(0, Math.min(100, percent))
  }

  private async fetchAndRender() {
    const email = getGlobals()?.customer?.email ?? ''
    if (!email) {
      this.state = 'unavailable'
      this.render()
      return
    }

    let account = getCachedRewardsAccount(email)
    if (!account || !hasLoyaltyData(account)) {
      const result = await fetchRewardsAccount(email)
      account = !result.ok ? null : getAccountFromResult(result)
    }
    this.applyAccount(account)
    this.render()
  }

  private renderLoading() {
    this.innerHTML = `
      <div class="${TIER_SPEND_INNER_CLASS}">
        <div class="flex items-center justify-center flex-1 w-full">
          <div class="size-[24px] text-brand-black" aria-hidden="true">
            <svg class="size-full animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        </div>
      </div>
    `
  }

  private render() {
    if (this.state === 'loading') {
      this.renderLoading()
      return
    }

    if (this.state === 'unavailable') {
      this.innerHTML = `
        <div class="${TIER_SPEND_INNER_CLASS}">
          <p class="paragraph-s">Your loyalty data is currently unavailable</p>
        </div>
      `
      return
    }

    const imageBlock =
      this.tierImageUrl !== ''
        ? `<div class="size-[32px] overflow-hidden rounded-full shrink-0"><img src="${escapeHtml(
            this.tierImageUrl
          )}" alt="" class="size-full object-cover" width="32" height="32"></div>`
        : ''

    const progressBlock = this.hasAvailableRewards
      ? (() => {
          const hasSpendAmount = Boolean(this.spendFormatted?.trim())
          const message = hasSpendAmount
            ? `<span class="font-medium">Spend ${escapeHtml(this.spendFormatted)}</span> to reach the next tier`
            : 'Your tier will update shortly'
          return `
      <div class="flex flex-col items-center justify-center gap-y-0.5 w-full">
        <p class="paragraph-s">${message}</p>
        <div class="w-full max-w-[308px] h-[4px] bg-system-warning/10 overflow-hidden rounded-full">
          <div class="h-full transition-all ${
            this.progressPercent >= 100 ? 'bg-system-success' : 'bg-system-warning'
          }" style="width: ${Math.round(this.progressPercent)}%"></div>
        </div>
      </div>
    `
        })()
      : ''

    const benefitsLinkBlock =
      this.benefitsTableUrl !== ''
        ? `<a href="${escapeHtml(
            this.benefitsTableUrl
          )}" class="btn btn-primary btn-text btn-s w-fit" aria-label="View your benefits">
          <span>View Benefits</span>
        </a>`
        : ''

    this.innerHTML = `
      <div class="${TIER_SPEND_INNER_CLASS}">
        <span class="paragraph-s font-heading font-medium uppercase">Your Current Tier</span>
        <div class="flex items-center gap-x-0.5">
          ${imageBlock}
          <p class="h2">${escapeHtml(this.tierName || '—')}</p>
        </div>
        ${progressBlock}
        ${benefitsLinkBlock}
      </div>
    `
  }
}

// --- Loyalty Rewards Grid ---

function getMetaString(v: string | { value?: string } | undefined): string {
  if (v == null) return ''
  return typeof v === 'string' ? v : (v.value ?? '') || ''
}

function richTextToHtml(node: RichTextNode | undefined): string {
  if (!node || typeof node !== 'object') return ''
  if (node.value != null && typeof node.value === 'string') {
    const text = escapeHtml(node.value)
    return node.bold ? `<strong>${text}</strong>` : text
  }
  if (!Array.isArray(node.children)) return ''
  const inner = node.children.map((c) => richTextToHtml(c)).join('')
  const type = node.type ?? ''
  if (type === 'root') return inner
  if (type === 'paragraph') return `<p>${inner}</p>`
  if (type === 'list' || type === 'list-item') return `<li>${inner}</li>`
  return inner
}

function getMetaBody(v: string | { value?: string } | RichTextNode | undefined): string {
  if (v == null) return ''
  if (typeof v === 'string') return v
  if (typeof (v as { value?: string }).value === 'string') return (v as { value: string }).value
  if ((v as RichTextNode).type === 'root' || Array.isArray((v as RichTextNode).children)) {
    return richTextToHtml(v as RichTextNode)
  }
  return ''
}

function findTierByName(tiers: LoyaltyTierMeta[] | undefined, tierName: string): LoyaltyTierMeta | null {
  if (!Array.isArray(tiers) || !tierName?.trim()) return null
  const nameLower = tierName.trim().toLowerCase()
  return tiers.find((t) => (t.tier_name ?? '').trim().toLowerCase() === nameLower) ?? null
}

function getTierGid(tier: LoyaltyTierMeta | null): string | null {
  if (!tier) return null
  const num = typeof tier.id === 'number' ? tier.id : parseInt(String(tier.id), 10)
  return Number.isNaN(num) ? null : `gid://shopify/Metaobject/${num}`
}

function getTierColours(tier: LoyaltyTierMeta | null): { background: string; text: string } {
  if (!tier) return { background: 'transparent', text: 'currentColor' }
  return {
    background: getMetaString(tier.tier_colour) || 'transparent',
    text: getMetaString(tier.text_colour) || 'currentColor'
  }
}

function getMetaRewardTierGids(reward: MetaRewardRaw): string[] {
  const raw = reward.tiers
  if (!Array.isArray(raw)) return []
  return raw.filter((g): g is string => typeof g === 'string' && g.startsWith('gid://'))
}

function getMetaRewardImageUrl(reward: MetaRewardRaw): string {
  return typeof reward.image === 'string' ? reward.image : ''
}

function formatExpiresAt(isoDate: string | undefined): string {
  if (!isoDate?.trim()) return ''
  const d = new Date(isoDate.trim())
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const LAZY_IMG_PLACEHOLDER =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUCB1j+Pjx438ACX0D04PrXz0AAAAASUVORK5CYII='

const COPY_URL_PREFIX = '#copy-['
const COPY_URL_SUFFIX = ']'

function getCopyCodeFromUrl(url: string): string | null {
  if (typeof url !== 'string' || !url.startsWith(COPY_URL_PREFIX)) return null
  const end = url.indexOf(COPY_URL_SUFFIX, COPY_URL_PREFIX.length)
  if (end === -1) return null
  return url.slice(COPY_URL_PREFIX.length, end)
}

export class LoyaltyRewardsGridElement extends HTMLElement {
  private state: 'loading' | 'ready' = 'loading'
  private tierName = ''
  private apiRewards: RewardsAccountReward[] = []
  private displayRewards: DisplayReward[] = []

  connectedCallback() {
    this.renderLoading()
    this.fetchAndRender()
    this.addEventListener('click', this.handleCopyClick)
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleCopyClick)
  }

  private handleCopyClick = (e: Event) => {
    const btn = (e.target as Element).closest?.('[data-copy-code]')
    if (!btn) return
    const code = btn.getAttribute('data-copy-code')
    const label = btn.getAttribute('data-copy-label')
    if (!code) return
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code).then(() => {
        const prev = btn.textContent
        btn.textContent = 'Copied!'
        setTimeout(() => {
          btn.textContent = label ?? prev ?? ''
        }, 2000)
      })
    }
  }

  private applyAccount(account: RewardsAccountResponse | null) {
    const g = getGlobals()
    this.tierName = (account?.tier_name ?? g?.customer?.tierName ?? '').trim()
    this.apiRewards = Array.isArray(account?.rewards) ? account.rewards : []

    const tiers = g?.metaobjects?.tiers
    const tier = findTierByName(tiers, this.tierName)
    const tierColours = getTierColours(tier)
    const currentTierGid = getTierGid(tier)

    const list = g?.metaobjects?.rewards ?? g?.metaobjects?.loyalty_rewards
    const metaRewards = Array.isArray(list) ? list.filter((r): r is MetaRewardRaw => r != null) : []

    const out: DisplayReward[] = []
    for (const raw of metaRewards) {
      const rewardTierGids = getMetaRewardTierGids(raw)
      if (!rewardTierGids.length || !currentTierGid || !rewardTierGids.includes(currentTierGid)) continue

      const ap21Desc = getMetaString(raw.ap21_loyalty_description)
      const apiMatch = ap21Desc ? this.apiRewards.find((r) => (r.description ?? '').trim() === ap21Desc.trim()) : null
      if (ap21Desc && !apiMatch) continue

      out.push({
        priority: getMetaNumber(raw.priority),
        title: getMetaString(raw.title),
        body: getMetaBody(raw.body),
        imageUrl: getMetaRewardImageUrl(raw),
        backgroundColour: tierColours.background,
        textColour: tierColours.text,
        ctaText: getMetaString(raw.cta_text),
        ctaUrl: getMetaString(raw.cta_url),
        expiresAt: apiMatch ? formatExpiresAt(apiMatch.expires_at) : ''
      })
    }

    this.displayRewards = out.sort((a, b) => a.priority - b.priority)
    this.state = 'ready'
  }

  private async fetchAndRender() {
    const email = getGlobals()?.customer?.email ?? ''
    if (!email) {
      this.applyAccount(null)
      this.render()
      return
    }

    let account = getCachedRewardsAccount(email)
    if (!account) {
      const result = await fetchRewardsAccount(email)
      account = getAccountFromResult(result)
    }
    this.applyAccount(account)
    this.render()
  }

  private renderLoading() {
    this.innerHTML = `
      <div class="flex flex-col items-center justify-center w-full min-h-[200px]">
        <div class="size-[24px] text-brand-black" aria-hidden="true">
          <svg class="size-full animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
    `
  }

  private render() {
    if (this.state === 'loading') {
      this.renderLoading()
      return
    }

    const hasRewards = this.displayRewards.length > 0
    const heading = hasRewards
      ? (this.getAttribute('data-rewards-heading') ?? '').trim()
      : (this.getAttribute('data-no-rewards-heading') ?? '').trim()

    const cards = this.displayRewards
      .map((r) => {
        const copyCode = r.ctaUrl ? getCopyCodeFromUrl(r.ctaUrl) : null
        const ctaBlock =
          r.ctaText && r.ctaUrl
            ? copyCode
              ? `<button type="button" class="btn btn-primary btn-fill btn-m w-fit" data-copy-code="${escapeHtml(
                  copyCode
                )}" data-copy-label="${escapeHtml(r.ctaText)}" aria-label="${escapeHtml(r.ctaText)}">${escapeHtml(
                  r.ctaText
                )}</button>`
              : `<a href="${escapeHtml(
                  r.ctaUrl
                )}" class="btn btn-primary btn-fill btn-m w-fit" aria-label="${escapeHtml(r.ctaText)}">${escapeHtml(
                  r.ctaText
                )}</a>`
            : ''
        return `
        <div class="flex flex-col items-center justify-center gap-1.5 w-full min-h-[256px] lg:min-h-[265px] text-center border border-[--border-colour] bg-[--bg-colour] px-2 py-2.5 rounded-8 overflow-hidden"
          style="--bg-colour: ${escapeHtml(r.backgroundColour)}; --border-colour: ${escapeHtml(r.textColour)};">
          ${
            r.imageUrl
              ? `<div class="size-[50px] shrink-0"><img src="${LAZY_IMG_PLACEHOLDER}" data-src="${escapeHtml(
                  r.imageUrl
                )}" alt="" class="lazy size-full object-contain" width="50" height="50"></div>`
              : ''
          }
          <div class="flex flex-col gap-y-0.5">
            <p class="h5">${escapeHtml(r.title)}</p>
            ${r.body ? `<div class="richtext paragraph-s">${r.body}</div>` : ''}
          </div>
          ${ctaBlock}
          ${r.expiresAt ? `<p class="paragraph-s font-medium">Expires on ${escapeHtml(r.expiresAt)}</p>` : ''}
        </div>`
      })
      .join('')

    this.innerHTML = `
      <div class="flex flex-col items-center justify-center gap-1.75 w-full">
        ${heading}
        ${
          hasRewards
            ? `<div class="grid lg:grid-cols-3 auto-rows-auto gap-0.5 lg:gap-1.5 lg:items-stretch w-full">${cards}</div>`
            : ''
        }
      </div>
    `

    if (typeof window !== 'undefined' && (window as any).lazyLoadInstance) {
      (window as any).lazyLoadInstance.update()
    }
  }
}

// --- Loyalty Header Badge ---

const DEFAULT_BADGE_TEXT = 'My Account'

export class LoyaltyHeaderBadgeElement extends HTMLElement {
  private defaultText = DEFAULT_BADGE_TEXT
  private initialClass = ''
  private abortController: AbortController | null = null

  connectedCallback() {
    this.initialClass = this.getAttribute('class') ?? ''
    const existing = this.textContent?.trim()
    this.defaultText = existing ?? DEFAULT_BADGE_TEXT
    if (!existing) {
      this.textContent = this.defaultText
    }

    this.applyRewardsFromGlobals()
    this.applyRewardsFromCache()
    this.fetchAndUpdate()
  }

  private setRewardsDisplay(formatted: string) {
    this.textContent = `Rewards: ${formatted}`
    const rewardsClass = this.getAttribute('data-rewards-class')
    if (rewardsClass !== null) this.className = rewardsClass
  }

  private setDefaultDisplay() {
    this.textContent = this.defaultText
    const defaultClass = this.getAttribute('data-default-class')
    this.className = defaultClass !== null ? defaultClass : this.initialClass
  }

  disconnectedCallback() {
    this.abortController?.abort()
  }

  /** Apply server-side availableRewards from gm-state first to avoid default → Rewards flash. */
  private applyRewardsFromGlobals() {
    const g = getGlobals()
    const raw = (g?.customer?.availableRewards ?? '').toString().trim()
    if (raw === '') return

    const amount = parseFloat(raw)
    if (Number.isNaN(amount) || amount <= 0) return

    const moneyFormat = g?.shopify?.moneyFormat ?? '${{amount}}'
    const cents = Math.round(amount * 100)
    const formatted = formatMoney(cents, moneyFormat as string)
    this.setRewardsDisplay(formatted)
  }

  /** Apply cached API rewards so badge is correct when theme already ran fetch on load. */
  private applyRewardsFromCache() {
    const g = getGlobals()
    const email = g?.customer?.email ?? ''
    if (!email) return

    const data = getCachedRewardsAccount(email)
    if (!data) return

    const raw = data.available_rewards?.trim() ?? ''
    if (raw === '') return

    const amount = parseFloat(raw)
    if (Number.isNaN(amount) || amount <= 0) return

    const moneyFormat = g?.shopify?.moneyFormat ?? '${{amount}}'
    const cents = Math.round(amount * 100)
    const formatted = formatMoney(cents, moneyFormat as string)
    this.setRewardsDisplay(formatted)
  }

  private async fetchAndUpdate() {
    const g = getGlobals()
    const email = g?.customer?.email ?? ''
    if (!email) return

    try {
      this.abortController = new AbortController()
      const result = await fetchRewardsAccount(email)
      this.abortController = null

      const data = getAccountFromResult(result)
      if (!data) {
        this.setDefaultDisplay()
        return
      }

      const raw = data.available_rewards?.trim() ?? ''
      const amount = raw === '' ? 0 : parseFloat(raw)
      if (Number.isNaN(amount) || amount <= 0) {
        this.setDefaultDisplay()
        return
      }

      const moneyFormat = g?.shopify?.moneyFormat ?? '${{amount}}'
      const cents = Math.round(amount * 100)
      const formatted = formatMoney(cents, moneyFormat as string)
      this.setRewardsDisplay(formatted)
    } catch {
      this.setDefaultDisplay()
    }
  }
}
